/**
 * Monitoring Service
 *
 * Central service for handling all operational monitoring functionality.
 * This service coordinates between the monitoring utilities and provides
 * a clean interface for the rest of the application.
 */

import {
  ActionType,
  ConfigValidator,
  DEFAULT_MONITORING_CONFIG,
} from '../types';
import { ActionTracker } from '../utils/ActionTracker';
import { ContextEnricher } from '../utils/ContextEnricher';
import { DatadogIntegration } from '../utils/DatadogIntegration';
import { TransactionCorrelator } from '../utils/TransactionCorrelator';

import type {
  ActionContext,
  ActionTrackingConfig,
  MonitoringContextProvider,
  PerformanceMetrics,
  WorkflowType,
} from '../types';

/**
 * Central monitoring service that coordinates all monitoring functionality
 */
export class MonitoringService {
  private actionTracker: ActionTracker;
  private transactionCorrelator: TransactionCorrelator;
  private contextEnricher: ContextEnricher;
  private datadogIntegration: DatadogIntegration;
  private monitoringConfig: ActionTrackingConfig;

  constructor(contextProvider: MonitoringContextProvider) {
    // Validate and set configuration
    this.monitoringConfig = ConfigValidator.validateConfig(
      DEFAULT_MONITORING_CONFIG,
    );

    // Initialize utilities with context provider
    this.contextEnricher = ContextEnricher.getInstance(contextProvider);
    this.datadogIntegration = DatadogIntegration.getInstance(
      this.monitoringConfig,
    );
    this.actionTracker = new ActionTracker(
      this.monitoringConfig,
      this.contextEnricher,
      this.datadogIntegration,
    );
    this.transactionCorrelator = new TransactionCorrelator(
      this.contextEnricher,
    );
  }

  /**
   * Start a new user action
   */
  startUserAction(
    actionName: string,
    actionType: ActionType,
    workflowId?: string,
    context?: Partial<ActionContext>,
    metadata?: Record<string, any>,
  ): string {
    if (!this.monitoringConfig.enabled || !this.actionTracker) {
      return '';
    }

    try {
      const transactionId = this.actionTracker.startAction(
        actionName,
        actionType,
        workflowId,
        context,
        metadata,
      );

      // Add to workflow if workflowId is provided
      if (workflowId && this.transactionCorrelator) {
        this.transactionCorrelator.addTransactionToWorkflow(
          workflowId,
          transactionId,
        );
      }

      return transactionId;
    } catch (error) {
      console.warn('Failed to start user action:', error);
      return '';
    }
  }

  /**
   * Complete a user action successfully
   */
  completeUserAction(
    transactionId: string,
    metadata?: Record<string, any>,
    performance?: Partial<PerformanceMetrics>,
  ): void {
    if (
      !this.monitoringConfig.enabled ||
      !this.actionTracker ||
      !transactionId
    ) {
      return;
    }

    try {
      this.actionTracker.completeAction(transactionId, metadata, performance);
    } catch (error) {
      console.warn('Failed to complete user action:', error);
    }
  }

  /**
   * Mark a user action as failed
   */
  failUserAction(
    transactionId: string,
    error: Error | string,
    metadata?: Record<string, any>,
  ): void {
    if (
      !this.monitoringConfig.enabled ||
      !this.actionTracker ||
      !transactionId
    ) {
      return;
    }

    try {
      this.actionTracker.failAction(transactionId, error, metadata);
    } catch (error) {
      console.warn('Failed to mark user action as failed:', error);
    }
  }

  /**
   * Cancel a user action
   */
  cancelUserAction(transactionId: string, reason?: string): void {
    if (
      !this.monitoringConfig.enabled ||
      !this.actionTracker ||
      !transactionId
    ) {
      return;
    }

    try {
      this.actionTracker.cancelAction(transactionId, reason);
    } catch (error) {
      console.warn('Failed to cancel user action:', error);
    }
  }

  /**
   * Start a new workflow
   */
  startWorkflow(
    workflowType: WorkflowType,
    metadata?: Record<string, any>,
    context?: Partial<ActionContext>,
  ): string {
    if (!this.monitoringConfig.enabled || !this.transactionCorrelator) {
      return '';
    }

    try {
      const workflowId = this.transactionCorrelator.startWorkflow(
        workflowType,
        metadata,
        context,
      );

      // Track workflow start event
      this.startUserAction(
        workflowType,
        ActionType.WORKFLOW,
        workflowId,
        context,
        metadata,
      );

      return workflowId;
    } catch (error) {
      console.warn('Failed to start workflow:', error);
      return '';
    }
  }

  /**
   * Complete a workflow
   */
  completeWorkflow(
    workflowId: string,
    success: boolean,
    metadata?: Record<string, any>,
  ): void {
    if (
      !this.monitoringConfig.enabled ||
      !this.transactionCorrelator ||
      !workflowId
    ) {
      return;
    }

    try {
      this.transactionCorrelator.completeWorkflow(
        workflowId,
        success,
        metadata,
      );

      // Get workflow type before completing (since it gets cleaned up)
      const workflowType =
        this.transactionCorrelator.getWorkflowType(workflowId);

      // Send workflow completion/failure event
      if (workflowType) {
        if (success) {
          // Start and immediately complete a workflow completion action
          const completionTransactionId = this.startUserAction(
            workflowType,
            ActionType.WORKFLOW,
            workflowId,
            undefined,
            metadata,
          );
          this.completeUserAction(completionTransactionId, metadata);
        } else {
          // Start and immediately fail a workflow failure action
          const failureTransactionId = this.startUserAction(
            workflowType,
            ActionType.WORKFLOW,
            workflowId,
            undefined,
            metadata,
          );
          this.failUserAction(
            failureTransactionId,
            'Workflow failed',
            metadata,
          );
        }
      }
    } catch (error) {
      console.warn('Failed to complete workflow:', error);
    }
  }

  /**
   * Cancel a workflow
   */
  cancelWorkflow(workflowId: string, reason?: string): void {
    if (
      !this.monitoringConfig.enabled ||
      !this.transactionCorrelator ||
      !workflowId
    ) {
      return;
    }

    try {
      this.transactionCorrelator.cancelWorkflow(workflowId, reason);

      // Get workflow type before cancelling (since it gets cleaned up)
      const workflowType =
        this.transactionCorrelator.getWorkflowType(workflowId);

      // Send workflow cancellation event
      if (workflowType) {
        // Start and immediately cancel a workflow cancellation action
        const cancellationTransactionId = this.startUserAction(
          workflowType,
          ActionType.WORKFLOW,
          workflowId,
          undefined,
          undefined,
        );
        this.cancelUserAction(cancellationTransactionId, reason);
      }
    } catch (error) {
      console.warn('Failed to cancel workflow:', error);
    }
  }

  /**
   * Update monitoring configuration
   */
  updateMonitoringConfig(newConfig: Partial<ActionTrackingConfig>): void {
    try {
      this.monitoringConfig = ConfigValidator.validateConfig({
        ...this.monitoringConfig,
        ...newConfig,
      });

      // Update all monitoring utilities with new config
      if (this.actionTracker) {
        this.actionTracker.updateConfig(this.monitoringConfig);
      }
      if (this.datadogIntegration) {
        this.datadogIntegration.updateConfig(this.monitoringConfig);
      }

      console.log('Monitoring configuration updated:', this.monitoringConfig);
    } catch (error) {
      console.warn('Failed to update monitoring configuration:', error);
    }
  }

  /**
   * Get current monitoring configuration
   */
  getMonitoringConfig(): ActionTrackingConfig {
    return { ...this.monitoringConfig };
  }

  /**
   * Track performance metrics
   */
  trackPerformance(
    actionName: string,
    metrics: PerformanceMetrics,
    context?: Partial<ActionContext>,
  ): void {
    if (!this.monitoringConfig.enabled || !this.actionTracker) {
      return;
    }

    try {
      const transactionId = this.startUserAction(
        actionName,
        ActionType.EVENT,
        undefined,
        context,
        undefined,
      );
      this.completeUserAction(transactionId, {}, metrics);
    } catch (error) {
      console.warn('Failed to track performance:', error);
    }
  }
}
